import { useState } from "react";
import { toast } from "sonner";
import { GitFork, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { cn } from "@/design/cn";
import { Stat, Sparkline, Pill, Button, EmptyState, AssuranceBadge } from "@/design/components";
import { useSeed, useMoney, useMateriality, useSetMateriality, useAssurance } from "@/domain/hooks";
import { useTrace } from "@/shell/TraceProvider";
import { deriveExceptions, pickVerificationSample, type ExceptionSeverity } from "@/domain/exceptions";
import { useVerifySpend } from "@/domain/useMutations";

// Semantic severity chips: high=danger, medium=warn, low=info — colour carries the state.
const SEV_TONE: Record<ExceptionSeverity, "danger" | "warn" | "info"> = { high: "danger", medium: "warn", low: "info" };
const SEV_LABEL: Record<ExceptionSeverity, string> = { high: "High", medium: "Medium", low: "Low" };

export function ExceptionsView() {
  const seed = useSeed();
  const cfg = useMateriality();
  const setCfg = useSetMateriality();
  const money = useMoney();
  const { verify, isPending: verifying } = useVerifySpend();
  const assuranceFor = useAssurance();
  const { openTrace } = useTrace();
  const [sampleIds, setSampleIds] = useState<string[] | null>(null);

  const exceptions = deriveExceptions(seed, cfg);
  const high = exceptions.filter((e) => e.severity === "high").length;
  const medium = exceptions.filter((e) => e.severity === "medium").length;
  const low = exceptions.filter((e) => e.severity === "low").length;

  const sample = sampleIds
    ? sampleIds.map((id) => seed.spendRequests.find((r) => r.id === id)).filter(Boolean)
    : null;

  function pullSample() {
    setSampleIds(pickVerificationSample(seed, 4).map((r) => r.id));
  }

  return (
    <div className="space-y-6">
      <p className="text-xs text-mute">
        Oversight reviews what breaches the materiality rules below, not every line. Tune the thresholds, work the exceptions, and pull a risk-weighted sample to verify · you can drill into any project from the sidebar at any time.
      </p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Open exceptions" value={String(exceptions.length)} subTone={exceptions.length ? "warn" : "ok"} sub={`${high} high · ${medium} medium · ${low} low`} trend={<Sparkline data={[3, 4, 5, 5, 6, exceptions.length]} />} />
        <Stat label="High severity" value={String(high)} subTone={high ? "bad" : "ok"} sub={high ? "review first" : "all clear"} trend={<Sparkline data={[1, 2, 2, 3, 3, high]} />} hero={high > 0} />
        <Stat label="Materiality" value={`${Math.round(cfg.spendReview / 1_000_000)}M`} unit="UGX" sub="single-spend review line" />
        <Stat label="Budget ceiling" value={`${Math.round(cfg.budgetUsed * 100)}%`} sub="project alert level" />
      </div>

      {/* Materiality config */}
      <section className="rounded-card border border-line bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-faint" />
          <h2 className="text-sm font-semibold text-ink">Materiality rules</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <NumField label="Review spend over (M UGX)" value={Math.round(cfg.spendReview / 1_000_000)} onChange={(v) => setCfg({ spendReview: v * 1_000_000 })} />
          <NumField label="Flag self-reported over (M UGX)" value={Math.round(cfg.gapValue / 1_000_000)} onChange={(v) => setCfg({ gapValue: v * 1_000_000 })} />
          <NumField label="Schedule drift over (days)" value={cfg.driftDays} onChange={(v) => setCfg({ driftDays: v })} />
          <NumField label="Budget consumed over (%)" value={Math.round(cfg.budgetUsed * 100)} onChange={(v) => setCfg({ budgetUsed: Math.min(100, v) / 100 })} />
        </div>
      </section>

      {/* Exception list */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-ink">Exceptions <span className="ml-1 text-xs font-normal text-faint tabular-nums">({exceptions.length})</span></h2>
        {exceptions.length === 0 ? (
          <EmptyState title="No exceptions against the current rules" body="Loosen the materiality thresholds above to surface more, or all observed spend is within tolerance." />
        ) : (
          <div className="overflow-hidden rounded-card border border-line bg-card">
            {exceptions.map((e) => (
              <div key={e.id} className={cn("flex flex-wrap items-center gap-3 border-b border-hairline py-3 pr-4 last:border-0 border-l-4 pl-3", e.severity === "high" ? "border-l-bad" : e.severity === "medium" ? "border-l-warn" : "border-l-info")}>
                <Pill variant={SEV_TONE[e.severity]} size="sm" className="shrink-0 uppercase tracking-wide">{SEV_LABEL[e.severity]}</Pill>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium text-ink">{e.title}</p>
                    <span className="text-meta text-faint">{e.ruleLabel}</span>
                  </div>
                  {e.body && <p className="truncate text-xs text-faint">{e.body}</p>}
                </div>
                {e.figure !== undefined && <span className="num text-sm font-medium tabular-nums text-ink">{money(e.figure)}</span>}
                <div className="flex gap-1.5">
                  {e.target && (
                    <Button size="sm" variant="ghost" aria-label="Trace source" onClick={() => openTrace(e.target!, { from: "Signals" })}><GitFork className="h-4 w-4" /> Trace source</Button>
                  )}
                  {e.spendId && (
                    <Button size="sm" variant="secondary" disabled={verifying} onClick={async () => { await verify(e.spendId!); toast.success("Verified"); }}>
                      <ShieldCheck className="h-4 w-4" /> Verify
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Verification sample */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-meta text-faint">Verification sample</h2>
          <Button size="sm" variant="secondary" onClick={pullSample}>Pull a risk-weighted sample</Button>
        </div>
        {!sample ? (
          <p className="text-sm text-faint">Pull a sample to select the highest-value unverified spend for a site visit or document check.</p>
        ) : sample.length === 0 ? (
          <EmptyState title="Nothing left to sample" body="All money-out spend is already independently verified." />
        ) : (
          <div className="overflow-hidden rounded-card border border-line bg-card">
            {sample.map((r) => (
              <div key={r!.id} className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-3 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{r!.title}</p>
                  <p className="text-xs text-faint">{seed.projects.find((p) => p.id === r!.projectId)?.name}</p>
                </div>
                <AssuranceBadge level={assuranceFor(r!)} />
                <span className="num ml-auto whitespace-nowrap text-sm font-medium tabular-nums text-ink">{money(r!.amount)}</span>
                <Button size="sm" variant={r!.verifiedById ? "ghost" : "secondary"} disabled={verifying || !!r!.verifiedById} onClick={async () => { await verify(r!.id); toast.success(`Verified '${r!.title}'`); }}>
                  <ShieldCheck className="h-4 w-4" /> {r!.verifiedById ? "Verified" : "Verify"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function NumField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-meta text-faint">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
        className="focus-ring w-full rounded-control border border-line px-2 py-1.5 text-sm tabular-nums text-ink"
      />
    </label>
  );
}
